// ===========================================================================
// PatientResultsDialog — dialog con los pacientes similares de una búsqueda.
//
// Lo abre PatientSearch tras pulsar "Buscar". Lista los matches (nombre, DOB,
// edad/sexo, Member ID, CIP, location, PCP); al hacer click en uno se dispara
// onSelect(patient) y se cierra. Maneja estados de carga, error y vacío.
// ===========================================================================
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  IconButton,
  List,
  ListItemButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { formatDate, safe } from "../../utils/format";

// Puntito de carrier: ACA azul, MED naranja, otro gris.
const carrierColor = (type) => {
  const t = String(type || "").toUpperCase();
  if (t === "ACA") return "#1976d2";
  if (t === "MED" || t === "MEDICARE") return "#ef6c00";
  return "#bdbdbd";
};

export default function PatientResultsDialog({
  open,
  loading,
  error,
  patients = [],
  hasMore = false,
  criteria,
  onSelect,
  onClose,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PersonSearchIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Select a patient
          </Typography>
        </Stack>
        {criteria && (
          <Typography variant="caption" color="text.secondary">
            {criteria}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, minHeight: 160 }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Searching patients…
            </Typography>
          </Stack>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : patients.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6, px: 3, textAlign: "center" }}>
            <PersonSearchIcon sx={{ fontSize: 40, color: "text.disabled" }} />
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              No matching patients
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Try a different name or date of birth.
            </Typography>
          </Stack>
        ) : (
          <List disablePadding>
            {patients.map((p, idx) => (
              <React.Fragment key={p.cipId || idx}>
                {idx > 0 && <Divider component="li" />}
                <ListItemButton onClick={() => onSelect(p)} sx={{ py: 1.25, px: 2, alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: carrierColor(p.memberType), flexShrink: 0 }}
                        title={p.memberType || "Unknown carrier"}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                        {p.memberFullName || "—"}
                      </Typography>
                      {p.status === 0 && (
                        <Chip label="Inactive" size="small" color="default" sx={{ height: 18, fontSize: "0.6rem" }} />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      DOB {formatDate(p.dateOfBirth)} · {safe(p.age)}y · {safe(p.gender)} · Member {safe(p.memberId)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      CIP {safe(p.cipId)} · {safe(p.medicalCenter)} · PCP {safe(p.pcpName)}
                    </Typography>
                  </Box>
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {loading || error
            ? ""
            : `${patients.length} result${patients.length === 1 ? "" : "s"}${hasMore ? "+ (refine to narrow)" : ""}`}
        </Typography>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
